import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square, Loader2, Volume2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpeechRecorder = ({ onTranscription, questionContext, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const startRecording = async () => {
    try {
      // Suppress exam integrity during mic permission dialog
      window.dispatchEvent(new Event('exam-integrity-suppress'));
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Resume integrity after mic granted (with grace period in handler)
      window.dispatchEvent(new Event('exam-integrity-resume'));
      streamRef.current = stream;
      chunks.current = [];
      setTranscription(null);
      setAudioUrl(null);
      setDuration(0);

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorder.current = recorder;

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      recorder.onstop = handleRecordingStop;
      recorder.start(100);
      setRecording(true);

      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      window.dispatchEvent(new Event('exam-integrity-resume'));
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && recording) {
      mediaRecorder.current.stop();
      setRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    }
  };

  const handleRecordingStop = async () => {
    const blob = new Blob(chunks.current, { type: 'audio/webm' });
    const url = URL.createObjectURL(blob);
    setAudioUrl(url);
    setTranscribing(true);

    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      const res = await axios.post(`${API}/speech/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTranscription(res.data.text);
      if (onTranscription) onTranscription(res.data.text);
      toast.success('Speech transcribed!');
    } catch (err) {
      toast.error('Transcription failed. Please try again.');
    } finally {
      setTranscribing(false);
    }
  };

  const resetRecording = () => {
    setTranscription(null);
    setAudioUrl(null);
    setDuration(0);
    if (onTranscription) onTranscription(null);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="border-2 border-gray-200 p-5" data-testid="speech-recorder">
      <div className="flex items-center gap-2 mb-3">
        <Volume2 size={18} className="text-[#002FA7]" />
        <span className="text-sm font-bold uppercase tracking-wider text-gray-600">Speaking Response</span>
      </div>

      {questionContext && (
        <p className="text-sm text-gray-500 mb-4">{questionContext}</p>
      )}

      {/* Recording Controls */}
      <div className="flex items-center gap-3 mb-4">
        {!recording && !transcribing && !transcription && (
          <Button
            onClick={startRecording}
            disabled={disabled}
            className="bg-red-500 hover:bg-red-600 text-white h-12 px-6"
            data-testid="start-recording-btn"
          >
            <Mic size={18} className="mr-2" />
            Start Recording
          </Button>
        )}

        {recording && (
          <>
            <Button
              onClick={stopRecording}
              className="bg-gray-800 hover:bg-gray-900 text-white h-12 px-6"
              data-testid="stop-recording-btn"
            >
              <Square size={16} className="mr-2" />
              Stop Recording
            </Button>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-mono text-red-600">{formatTime(duration)}</span>
            </div>
          </>
        )}

        {transcribing && (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Transcribing your speech...</span>
          </div>
        )}

        {transcription && (
          <Button
            onClick={resetRecording}
            variant="outline"
            className="btn-sharp h-10"
            data-testid="retry-recording-btn"
          >
            <RotateCcw size={14} className="mr-2" />
            Record Again
          </Button>
        )}
      </div>

      {/* Playback */}
      {audioUrl && (
        <div className="mb-4">
          <audio controls src={audioUrl} className="w-full h-10" data-testid="audio-playback" />
        </div>
      )}

      {/* Transcription Result */}
      {transcription && (
        <div className="bg-blue-50 border border-blue-200 p-4" data-testid="transcription-result">
          <div className="text-xs font-semibold text-blue-600 mb-1 uppercase">Your Response (Transcribed)</div>
          <p className="text-sm text-gray-800">{transcription}</p>
        </div>
      )}
    </div>
  );
};

export default SpeechRecorder;
